import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import {
    Sparkles, Loader2, Send, Trash2, Settings, Facebook, Instagram,
    MessageCircle, Copy, CheckCircle2, ArrowLeft, Image as ImageIcon, CalendarClock,
    Heart, Eye, MousePointerClick, ChevronLeft, ChevronRight, X, FolderOpen, Upload,
    Twitter, Music2, Linkedin,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import MarketWatchTicker from "../components/social/MarketWatchTicker";
import { useGearsVisible } from "../lib/gearVisibility";

/**
 * Jul-2026 (User-Wunsch) — Social-Media-Studio: Bikevinci generiert Beiträge
 * (Text + Hashtags + Bildauswahl) je Kanal; Beitrags-Archiv mit Status.
 * Posten ist vorbereitet: mit Meta-Token echt, ohne Token SIMULIERT.
 */
const CHANNEL_META = {
    facebook: { label: "Facebook", icon: Facebook, cls: "bg-blue-600" },
    instagram: { label: "Instagram", icon: Instagram, cls: "bg-pink-600" },
    whatsapp: { label: "WhatsApp", icon: MessageCircle, cls: "bg-emerald-600" },
    x: { label: "X (Twitter)", icon: Twitter, cls: "bg-neutral-900" },
    tiktok: { label: "TikTok", icon: Music2, cls: "bg-fuchsia-700" },
    linkedin: { label: "LinkedIn", icon: Linkedin, cls: "bg-[#0A66C2]" },
};
const STATUS_BADGE = {
    draft: { label: "Entwurf", cls: "bg-gray-100 text-gray-700" },
    posted: { label: "Gepostet", cls: "bg-emerald-100 text-emerald-800" },
    simulated: { label: "Simuliert", cls: "bg-amber-100 text-amber-800" },
    scheduled: { label: "Geplant", cls: "bg-blue-100 text-blue-800" },
};

export default function SocialMediaStudioPage() {
    const gearsOk = useGearsVisible();

    const [listings, setListings] = useState([]);
    const [posts, setPosts] = useState([]);
    const [settings, setSettings] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    // Generator-Formular
    const [listingId, setListingId] = useState("");
    const [topic, setTopic] = useState("");
    const [tone, setTone] = useState("locker");
    const [channels, setChannels] = useState({ facebook: true, instagram: true, whatsapp: false, x: false, tiktok: false, linkedin: false });
    const [generating, setGenerating] = useState(false);
    const [drafts, setDrafts] = useState([]);
    const [images, setImages] = useState([]);
    const [selectedImage, setSelectedImage] = useState("");
    const [busyId, setBusyId] = useState(null);
    const [genImage, setGenImage] = useState(false);
    const [imgSlogan, setImgSlogan] = useState("");
    const [imgPrice, setImgPrice] = useState("");
    const [scheduleId, setScheduleId] = useState(null);
    const [scheduleAt, setScheduleAt] = useState("");
    // Jul-2026 (User-Wunsch) — Erfolgsstatistik je Post + Wochen-Navigation im Kalender
    const [insights, setInsights] = useState({});
    const [weekOffset, setWeekOffset] = useState(0);
    const [bestTimes, setBestTimes] = useState([]);

    const load = useCallback(async () => {
        try {
            const [l, p, s] = await Promise.all([
                api.get("/listings/mine?limit=100"),
                api.get("/social/posts"),
                api.get("/social/settings"),
            ]);
            setListings(l.data?.listings || l.data || []);
            setPosts(p.data?.posts || []);
            setSettings(s.data);
        } catch { toast.error("Laden fehlgeschlagen"); }
        try {
            const [insR, btR] = await Promise.all([
                api.get("/social/insights"),
                api.get("/social/best-times"),
            ]);
            setInsights(insR.data?.insights || {});
            setBestTimes(btR.data?.recommendations || []);
        } catch { /* Statistik optional */ }
    }, []);
    useEffect(() => { load(); }, [load]);

    const generate = async () => {
        const chosen = Object.keys(channels).filter((c) => channels[c]);
        if (!chosen.length) { toast.error("Bitte mindestens einen Kanal wählen"); return; }
        setGenerating(true);
        setDrafts([]);
        try {
            const { data } = await api.post("/social/generate", {
                listing_id: listingId || null, topic: topic || null, tone, channels: chosen,
            });
            setDrafts((data.posts || []).map((p) => ({ ...p, text: p.text, hashtags: p.hashtags || [] })));
            setImages(data.images || []);
            setSelectedImage((data.images || [])[0] || "");
            if (!(data.posts || []).length) toast.error("Keine Beiträge generiert — bitte erneut versuchen");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Generierung fehlgeschlagen");
        } finally { setGenerating(false); }
    };

    const saveDraft = async (d) => {
        try {
            const { data } = await api.post("/social/posts", {
                channel: d.channel, text: d.text, hashtags: d.hashtags,
                image_url: selectedImage || null, listing_id: listingId || null,
            });
            toast.success(data.post?.status === "scheduled"
                ? `Gespeichert & automatisch geplant: ${new Date(data.post.scheduled_at).toLocaleString("de-DE")}`
                : "Im Archiv gespeichert");
            setDrafts((arr) => arr.filter((x) => x !== d));
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    };

    const publish = async (post) => {
        setBusyId(post.id);
        try {
            const { data } = await api.post(`/social/posts/${post.id}/publish`);
            toast.success(data.simulated
                ? "Posten SIMULIERT — hinterlege einen Meta-Token in den Einstellungen für echtes Posten"
                : "Beitrag wurde gepostet");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Posten fehlgeschlagen"); }
        finally { setBusyId(null); }
    };

    const removePost = async (post) => {
        try { await api.delete(`/social/posts/${post.id}`); await load(); }
        catch { toast.error("Löschen fehlgeschlagen"); }
    };

    // Jul-2026 (User-Wunsch) — KI-Aktionsgrafik, wenn kein Fahrzeugfoto passt
    const generateImage = async () => {
        setGenImage(true);
        try {
            const { data } = await api.post("/social/generate-image", {
                listing_id: listingId || null, topic: topic || null,
                slogan: imgSlogan || null, price_text: imgPrice || null,
            }, { timeout: 150000 });
            setImages((arr) => [data.image_url, ...arr]);
            setSelectedImage(data.image_url);
            toast.success("KI-Grafik erstellt und als Bild ausgewählt");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bildgenerierung fehlgeschlagen");
        } finally { setGenImage(false); }
    };

    // Jul-2026 (User-Wunsch) — Post-Planung zum Wunschtermin
    const schedulePost = async (post) => {
        if (!scheduleAt) { toast.error("Bitte Zeitpunkt wählen"); return; }
        setBusyId(post.id);
        try {
            await api.post(`/social/posts/${post.id}/schedule`, {
                scheduled_at: new Date(scheduleAt).toISOString(),
            });
            toast.success("Beitrag geplant — wird automatisch veröffentlicht");
            setScheduleId(null);
            setScheduleAt("");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Planung fehlgeschlagen"); }
        finally { setBusyId(null); }
    };
    const unschedulePost = async (post) => {
        setBusyId(post.id);
        try {
            await api.post(`/social/posts/${post.id}/unschedule`);
            toast.success("Planung aufgehoben");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Aufheben fehlgeschlagen"); }
        finally { setBusyId(null); }
    };

    // Jul-2026 (User-Wunsch) — Bikevinci-Empfehlung mit 1 Klick ins Planungsfeld übernehmen
    const applyBestTime = (post) => {
        const bt = bestTimes.find((b) => b.channel === post.channel);
        if (!bt?.next_slot) { toast.error("Keine Empfehlung verfügbar"); return; }
        const d = new Date(bt.next_slot);
        const pad = (n) => String(n).padStart(2, "0");
        setScheduleAt(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
        toast.success(`Bikevinci empfiehlt: ${bt.label}`);
    };

    const copyText = (p) => {
        const t = p.text + (p.hashtags?.length ? "\n\n" + p.hashtags.join(" ") : "");
        navigator.clipboard?.writeText(t).then(() => toast.success("In Zwischenablage kopiert"));
    };

    // Jul-2026 (User-Wunsch) — Automatisierung: Auto-Planung & Auto-Content
    const [autoBusy, setAutoBusy] = useState(false);
    const saveAutomation = async (patch) => {
        try {
            await api.post("/social/settings", patch);
            setSettings((s) => ({ ...s, ...patch }));
            toast.success("Gespeichert");
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    };
    const runAutoContent = async () => {
        setAutoBusy(true);
        try {
            const { data } = await api.post("/social/auto-content/run-now", {}, { timeout: 240000 });
            toast.success(data.review
                ? `${data.posts?.length || 0} Beiträge zu „${data.brand}" erstellt — warten auf Ihre Freigabe`
                : `${data.posts?.length || 0} Beiträge zu „${data.brand}" erstellt & zur besten Zeit eingeplant`);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Auto-Content fehlgeschlagen"); }
        finally { setAutoBusy(false); }
    };

    // Jul-2026 (User-Wunsch) — Auto-Beitrag freigeben → automatisch zur besten Zeit planen
    const approvePost = async (post) => {
        setBusyId(post.id);
        try {
            const { data } = await api.post(`/social/posts/${post.id}/approve`);
            toast.success(`Freigegeben — geplant für ${new Date(data.scheduled_at).toLocaleString("de-DE")}`);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Freigabe fehlgeschlagen"); }
        finally { setBusyId(null); }
    };

    // Jul-2026 (User-Wunsch) — Themen-Wunschliste für Bikevinci Auto-Content
    const [newTopic, setNewTopic] = useState("");
    const addTopic = () => {
        const t = newTopic.trim();
        if (!t) return;
        const list = [...(settings?.topic_wishlist || [])];
        if (!list.includes(t)) list.push(t);
        saveAutomation({ topic_wishlist: list });
        setNewTopic("");
    };
    const removeTopic = (t) => saveAutomation({ topic_wishlist: (settings?.topic_wishlist || []).filter((x) => x !== t) });

    // Jul-2026 (User-Wunsch) — Bilddatenbank-Zugriff + Foto-Upload für Beiträge
    const [showLibrary, setShowLibrary] = useState(false);
    const [library, setLibrary] = useState([]);
    const [uploading, setUploading] = useState(false);
    const openLibrary = async () => {
        setShowLibrary(true);
        try {
            const { data } = await api.get("/social/image-library");
            setLibrary(data.sections || []);
        } catch { toast.error("Bilddatenbank konnte nicht geladen werden"); }
    };
    const pickLibraryImage = (url) => {
        setImages((arr) => (arr.includes(url) ? arr : [url, ...arr]));
        setSelectedImage(url);
        setShowLibrary(false);
        toast.success("Bild übernommen — wird beim Speichern an den Beitrag angehängt");
    };
    const uploadPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload/image", fd,
                { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
            pickLibraryImage(data.url);
        } catch (err) { toast.error(err.response?.data?.detail || "Upload fehlgeschlagen"); }
        finally { setUploading(false); e.target.value = ""; }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-8" data-testid="social-media-studio">
            <Link to="/dashboard" className="text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-black inline-flex items-center gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
            </Link>
            <div className="flex items-center justify-between gap-3 flex-wrap mt-2 mb-6">
                <h1 className="font-display font-black text-3xl flex items-center gap-2">
                    <Sparkles className="w-7 h-7 text-[#FFCC00]" /> Social-Media-Studio
                </h1>
                {gearsOk && <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="social-settings-btn">
                    <Settings className="w-4 h-4 mr-1" /> Kanäle & Token
                    {settings && !settings.has_meta_token && (
                        <span className="ml-2 text-[10px] font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm">SIMULIERT</span>
                    )}
                </Button>}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                {/* Generator */}
                <div className="border-2 border-gray-200 rounded-sm p-4 space-y-3 bg-white">
                    <h2 className="font-display font-black text-lg">Beitrag generieren (Bikevinci)</h2>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Fahrzeug (optional)</label>
                        <select value={listingId} onChange={(e) => setListingId(e.target.value)}
                                className="w-full mt-1 h-9 rounded-sm border border-gray-300 bg-white px-2 text-sm"
                                data-testid="social-listing-select">
                            <option value="">— ohne Fahrzeug / freies Thema —</option>
                            {listings.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Thema / Anlass (optional)</label>
                        <Input value={topic} onChange={(e) => setTopic(e.target.value)}
                               placeholder="z. B. Frühjahrs-Check-Aktion, neues Modell eingetroffen …"
                               data-testid="social-topic-input" />
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500 block">Tonalität</label>
                            <select value={tone} onChange={(e) => setTone(e.target.value)}
                                    className="h-9 rounded-sm border border-gray-300 bg-white px-2 text-sm mt-1">
                                <option value="locker">Locker</option>
                                <option value="serioes">Seriös</option>
                                <option value="verkaufsstark">Verkaufsstark</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500 block mb-1">Kanäle</label>
                            <div className="flex items-center gap-3 flex-wrap">
                                {Object.keys(CHANNEL_META).map((c) => (
                                    <label key={c} className="flex items-center gap-1.5 text-sm cursor-pointer">
                                        <input type="checkbox" checked={channels[c]}
                                               onChange={(e) => setChannels((s) => ({ ...s, [c]: e.target.checked }))}
                                               className="h-4 w-4 accent-[#111827]" data-testid={`social-channel-${c}`} />
                                        {CHANNEL_META[c].label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button onClick={generate} disabled={generating}
                                className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm"
                                data-testid="social-generate-btn">
                            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                            {generating ? "Generiere …" : "Beiträge generieren"}
                        </Button>
                        <Button variant="outline" onClick={generateImage} disabled={genImage}
                                title="Bikevinci erstellt eine Aktionsgrafik, wenn kein Fahrzeugfoto passt (dauert bis zu 2 Min.)"
                                data-testid="social-generate-image-btn">
                            {genImage ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ImageIcon className="w-4 h-4 mr-1" />}
                            {genImage ? "Erstelle Grafik …" : "KI-Bild erstellen"}
                        </Button>
                        <Button variant="outline" onClick={openLibrary} data-testid="social-image-library-btn">
                            <FolderOpen className="w-4 h-4 mr-1" /> Bilddatenbank
                        </Button>
                        <label className="inline-flex items-center h-9 px-3 border border-gray-300 rounded-sm text-sm font-medium cursor-pointer bg-white hover:bg-gray-50"
                               data-testid="social-upload-photo-btn">
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                            {uploading ? "Lade hoch …" : "Foto hochladen"}
                            <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto}
                                   disabled={uploading} data-testid="social-upload-input" />
                        </label>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Slogan im Bild (optional)</label>
                            <Input value={imgSlogan} onChange={(e) => setImgSlogan(e.target.value)} maxLength={120}
                                   placeholder="z. B. Jetzt Probefahren!" data-testid="social-img-slogan" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Aktionspreis im Bild (optional)</label>
                            <Input value={imgPrice} onChange={(e) => setImgPrice(e.target.value)} maxLength={60}
                                   placeholder="z. B. ab 2.999 €" data-testid="social-img-price" />
                        </div>
                    </div>

                    {images.length > 0 && (
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-500 block mb-1">Bild wählen</label>
                            <div className="flex gap-2 flex-wrap">
                                {images.slice(0, 8).map((img) => (
                                    <button key={img} type="button" onClick={() => setSelectedImage(img)}
                                            className={`w-16 h-16 rounded-sm overflow-hidden border-2 ${selectedImage === img ? "border-[#FFCC00]" : "border-transparent"}`}>
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {drafts.map((d, i) => {
                        const M = CHANNEL_META[d.channel] || CHANNEL_META.facebook;
                        return (
                            <div key={`${d.channel}-${i}`} className="border border-gray-200 rounded-sm p-3 space-y-2" data-testid={`social-draft-${d.channel}`}>
                                <span className={`inline-flex items-center gap-1 text-[10px] font-bold text-white px-2 py-0.5 rounded-sm ${M.cls}`}>
                                    <M.icon className="w-3 h-3" /> {M.label}
                                </span>
                                <textarea value={d.text} rows={4}
                                          onChange={(e) => setDrafts((arr) => arr.map((x) => (x === d ? { ...x, text: e.target.value } : x)))}
                                          className="w-full rounded-sm border border-gray-300 px-2 py-1.5 text-sm resize-y" />
                                {d.hashtags?.length > 0 && (
                                    <p className="text-xs text-blue-700">{d.hashtags.join(" ")}</p>
                                )}
                                <Button size="sm" variant="outline" onClick={() => saveDraft(d)} data-testid={`social-save-${d.channel}`}>
                                    Ins Archiv speichern
                                </Button>
                            </div>
                        );
                    })}
                </div>

                {/* Archiv */}
                <div className="border-2 border-gray-200 rounded-sm p-4 bg-white">
                    <h2 className="font-display font-black text-lg mb-3">Beitrags-Archiv</h2>
                    {posts.length === 0 && <p className="text-sm text-gray-500">Noch keine Beiträge gespeichert.</p>}
                    <ul className="space-y-3">
                        {posts.map((p) => {
                            const M = CHANNEL_META[p.channel] || CHANNEL_META.facebook;
                            const S = STATUS_BADGE[p.status] || STATUS_BADGE.draft;
                            return (
                                <li key={p.id} className="border border-gray-200 rounded-sm p-3" data-testid={`social-post-${p.id}`}>
                                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold text-white px-2 py-0.5 rounded-sm ${M.cls}`}>
                                            <M.icon className="w-3 h-3" /> {M.label}
                                        </span>
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${S.cls}`} data-testid={`social-post-status-${p.id}`}>{S.label}</span>
                                        {p.needs_approval && (
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-sm bg-orange-100 text-orange-800"
                                                  data-testid={`social-approval-badge-${p.id}`}>Freigabe ausstehend</span>
                                        )}
                                        {p.status === "scheduled" && p.scheduled_at && (
                                            <span className="text-[10px] font-bold text-blue-700" data-testid={`social-scheduled-at-${p.id}`}>
                                                → {new Date(p.scheduled_at).toLocaleString("de-DE")}
                                            </span>
                                        )}
                                        {p.posted_at && <span className="text-[10px] text-gray-400">{new Date(p.posted_at).toLocaleString("de-DE")}</span>}
                                    </div>
                                    {p.image_url && <img src={p.image_url} alt="" className="w-20 h-20 object-cover rounded-sm mb-1.5" />}
                                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{p.text}</p>
                                    {p.hashtags?.length > 0 && <p className="text-xs text-blue-700 mt-1">{p.hashtags.join(" ")}</p>}
                                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                                        {p.needs_approval && (
                                            <Button size="sm" onClick={() => approvePost(p)} disabled={busyId === p.id}
                                                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-sm h-8"
                                                    data-testid={`social-approve-${p.id}`}>
                                                {busyId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Freigeben & planen
                                            </Button>
                                        )}
                                        {p.status !== "posted" && p.status !== "scheduled" && (
                                            <Button size="sm" onClick={() => publish(p)} disabled={busyId === p.id}
                                                    className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm h-8"
                                                    data-testid={`social-publish-${p.id}`}>
                                                {busyId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Jetzt posten
                                            </Button>
                                        )}
                                        {p.status !== "posted" && p.status !== "scheduled" && (
                                            <Button size="sm" variant="outline" className="h-8"
                                                    onClick={() => { setScheduleId(scheduleId === p.id ? null : p.id); setScheduleAt(""); }}
                                                    data-testid={`social-schedule-toggle-${p.id}`}>
                                                <CalendarClock className="w-3.5 h-3.5 mr-1" /> Planen
                                            </Button>
                                        )}
                                        {p.status === "scheduled" && (
                                            <Button size="sm" variant="outline" className="h-8 text-amber-700 border-amber-300"
                                                    onClick={() => unschedulePost(p)} disabled={busyId === p.id}
                                                    data-testid={`social-unschedule-${p.id}`}>
                                                Planung aufheben
                                            </Button>
                                        )}
                                        <Button size="sm" variant="outline" className="h-8" onClick={() => copyText(p)} data-testid={`social-copy-${p.id}`}>
                                            <Copy className="w-3.5 h-3.5 mr-1" /> Kopieren
                                        </Button>
                                        <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200" onClick={() => removePost(p)} data-testid={`social-delete-${p.id}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                        {p.status === "simulated" && (
                                            <span className="text-[11px] text-amber-700">Zum echten Posten Meta-Token hinterlegen — bis dahin Text kopieren & manuell posten.</span>
                                        )}
                                        {p.status === "posted" && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                                    </div>
                                    {scheduleId === p.id && (
                                        <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid={`social-schedule-form-${p.id}`}>
                                            <input
                                                type="datetime-local"
                                                value={scheduleAt}
                                                onChange={(e) => setScheduleAt(e.target.value)}
                                                className="h-8 rounded-sm border border-gray-300 px-2 text-xs bg-white"
                                                data-testid={`social-schedule-input-${p.id}`}
                                            />
                                            <Button size="sm" variant="outline" className="h-8 text-amber-700 border-amber-300"
                                                    onClick={() => applyBestTime(p)}
                                                    data-testid={`social-schedule-best-${p.id}`}>
                                                <Sparkles className="w-3.5 h-3.5 mr-1" /> Beste Zeit
                                            </Button>
                                            <Button size="sm" disabled={busyId === p.id || !scheduleAt}
                                                    onClick={() => schedulePost(p)}
                                                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-sm h-8"
                                                    data-testid={`social-schedule-save-${p.id}`}>
                                                {busyId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Termin planen"}
                                            </Button>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>

            <SocialSettingsDialog open={showSettings} onClose={() => { setShowSettings(false); load(); }} settings={settings} />

            {/* Jul-2026 (User-Wunsch) — Bilddatenbank-Auswahl für Beiträge */}
            <Dialog open={showLibrary} onOpenChange={(o) => !o && setShowLibrary(false)}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="social-image-library-dialog">
                    <DialogHeader>
                        <DialogTitle>Bilddatenbank — Bild für den Beitrag wählen</DialogTitle>
                    </DialogHeader>
                    {library.length === 0 && (
                        <p className="text-sm text-gray-500">Keine Bilder gefunden — laden Sie zuerst Fotos hoch oder pflegen Sie die Bilddatenbank.</p>
                    )}
                    {library.map((sec) => (
                        <div key={sec.key} className="mb-3">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">{sec.label}</h3>
                            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                                {sec.items.map((it, i) => (
                                    <button key={`${sec.key}-${i}`} type="button" onClick={() => pickLibraryImage(it.url)} title={it.name}
                                            className="aspect-square rounded-sm overflow-hidden border-2 border-transparent hover:border-[#FFCC00] focus:border-[#FFCC00]"
                                            data-testid={`social-library-img-${sec.key}-${i}`}>
                                        <img src={it.url} alt={it.name} className="w-full h-full object-cover" loading="lazy" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </DialogContent>
            </Dialog>

            {/* Jul-2026 (User-Wunsch) — Automatisierung: Auto-Planung + wöchentlicher Auto-Content */}
            <div className="border-2 border-gray-200 rounded-sm p-4 bg-white mt-6" data-testid="social-automation">
                <h2 className="font-display font-black text-base mb-3 flex items-center gap-2">
                    <CalendarClock className="w-4 h-4" /> Automatisierung
                </h2>
                <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={!!settings?.auto_schedule}
                               onChange={(e) => saveAutomation({ auto_schedule: e.target.checked })}
                               className="h-4 w-4 accent-[#111827]" data-testid="social-auto-schedule-toggle" />
                        <span><b>Auto-Planung</b> — neue Beiträge automatisch zur besten Zeit einplanen</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={!!settings?.auto_content_enabled}
                               onChange={(e) => saveAutomation({ auto_content_enabled: e.target.checked })}
                               className="h-4 w-4 accent-[#111827]" data-testid="social-auto-content-toggle" />
                        <span><b>Auto-Content</b> — wöchentlich Beiträge zu aktuellen Themen Ihrer Haupt-Marken</span>
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={!!settings?.auto_content_review}
                               onChange={(e) => saveAutomation({ auto_content_review: e.target.checked })}
                               className="h-4 w-4 accent-[#111827]" data-testid="social-auto-review-toggle" />
                        <span><b>Freigabe vor Planung</b> — Auto-Beiträge erst zur Freigabe vorlegen</span>
                    </label>
                    <Button size="sm" variant="outline" onClick={runAutoContent} disabled={autoBusy}
                            title="Bikevinci erstellt sofort Beiträge zur nächsten Haupt-Marke und plant sie zur besten Zeit ein"
                            data-testid="social-auto-content-run">
                        {autoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                        {autoBusy ? "Generiere …" : "Jetzt generieren"}
                    </Button>
                </div>
                <p className="text-[11px] text-gray-500 mt-2">Haupt-Marken = favorisierte Marken aus dem Händlerprofil, sonst die häufigsten Marken im Bestand. Bikevinci rotiert wöchentlich durch die Marken.</p>
                <div className="mt-3 pt-3 border-t border-gray-100">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Themen-Wunschliste — Bikevinci bevorzugt diese Themen (rotierend)</label>
                    <div className="flex items-center gap-2 mt-1.5">
                        <Input value={newTopic} onChange={(e) => setNewTopic(e.target.value)}
                               onKeyDown={(e) => e.key === "Enter" && addTopic()}
                               placeholder="z. B. Saisonstart, Hausmesse, Winter-Einlagerung …"
                               className="max-w-sm h-8" data-testid="social-topic-wishlist-input" />
                        <Button size="sm" variant="outline" className="h-8" onClick={addTopic} data-testid="social-topic-wishlist-add">
                            Hinzufügen
                        </Button>
                    </div>
                    {(settings?.topic_wishlist || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="social-topic-wishlist">
                            {settings.topic_wishlist.map((t) => (
                                <span key={t} className="inline-flex items-center gap-1 text-xs font-bold bg-gray-100 px-2 py-1 rounded-sm">
                                    {t}
                                    <button onClick={() => removeTopic(t)} className="text-gray-400 hover:text-red-600"
                                            data-testid={`social-topic-remove-${t}`} title="Thema entfernen">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Jul-2026 (User-Wunsch) — Bikevinci-Empfehlung: Beste Posting-Zeit je Kanal */}
            {bestTimes.length > 0 && (
                <div className="border-2 border-[#FFCC00] rounded-sm p-4 bg-yellow-50/60 mt-6" data-testid="social-best-times">
                    <h2 className="font-display font-black text-base mb-2 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" /> Bikevinci-Empfehlung: Beste Posting-Zeit
                    </h2>
                    <div className="flex flex-wrap gap-2">
                        {bestTimes.map((bt) => {
                            const M = CHANNEL_META[bt.channel] || CHANNEL_META.facebook;
                            return (
                                <div key={bt.channel} className="flex items-center gap-2 border border-gray-200 bg-white rounded-sm px-3 py-2"
                                     data-testid={`social-best-time-${bt.channel}`}>
                                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold text-white px-2 py-0.5 rounded-sm ${M.cls}`}>
                                        <M.icon className="w-3 h-3" /> {M.label}
                                    </span>
                                    <span className="text-sm font-bold">{bt.label}</span>
                                    <span className="text-[10px] text-gray-500">
                                        {bt.based_on === "eigene" ? `aus ${bt.posts_analyzed} eigenen Posts gelernt` : "Branchen-Richtwert"}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-[11px] text-gray-500 mt-2">Beim Planen eines Beitrags übernimmt der Button „Beste Zeit" automatisch den nächsten empfohlenen Termin.</p>
                </div>
            )}

            {/* Jul-2026 (User-Wunsch) — Posting-Kalender: Wochenansicht geplanter + geposteter Beiträge inkl. Erfolgsstatistik */}
            <div className="border-2 border-gray-200 rounded-sm p-4 bg-white mt-6" data-testid="social-calendar">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h2 className="font-display font-black text-lg flex items-center gap-2">
                        <CalendarClock className="w-5 h-5" /> Posting-Kalender
                    </h2>
                    <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setWeekOffset((o) => o - 1)} data-testid="social-calendar-prev">
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-xs font-bold text-gray-600 min-w-[110px] text-center" data-testid="social-calendar-range">
                            {weekOffset === 0 ? "Diese Woche" : weekOffset < 0 ? `${-weekOffset} Wo. zurück` : `${weekOffset} Wo. voraus`}
                        </span>
                        <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setWeekOffset((o) => o + 1)} data-testid="social-calendar-next">
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                    {Array.from({ length: 7 }).map((_, i) => {
                        const day = new Date();
                        day.setDate(day.getDate() + i + weekOffset * 7);
                        const dayKey = day.toDateString();
                        const isToday = weekOffset === 0 && i === 0;
                        const dayPosts = posts.filter((p) => {
                            const when = p.status === "scheduled" ? p.scheduled_at : p.posted_at;
                            return when && new Date(when).toDateString() === dayKey;
                        }).sort((a, b) => new Date(a.scheduled_at || a.posted_at) - new Date(b.scheduled_at || b.posted_at));
                        return (
                            <div key={i} className={`border rounded-sm p-2 min-h-[90px] ${isToday ? "border-[#FFCC00] bg-yellow-50/40" : "border-gray-200"}`}
                                 data-testid={`social-calendar-day-${i}`}>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                    {day.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}
                                    {isToday && " · Heute"}
                                </div>
                                <div className="mt-1 space-y-1">
                                    {dayPosts.length === 0 && <div className="text-[10px] text-gray-300">—</div>}
                                    {dayPosts.map((p) => {
                                        const M = CHANNEL_META[p.channel] || CHANNEL_META.facebook;
                                        const when = p.status === "scheduled" ? p.scheduled_at : p.posted_at;
                                        const stats = p.status !== "scheduled" ? insights[p.id] : null;
                                        return (
                                            <div key={p.id} className={`rounded-sm px-1.5 py-1 text-white ${M.cls}`} title={p.text}
                                                 data-testid={`social-calendar-post-${p.id}`}>
                                                <div className="text-[10px] font-bold flex items-center gap-1">
                                                    <M.icon className="w-3 h-3" />
                                                    {new Date(when).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                                                    {p.status === "scheduled" && <span className="opacity-80">· geplant</span>}
                                                </div>
                                                <div className="text-[10px] truncate opacity-90">{p.text}</div>
                                                {stats && (
                                                    <div className="flex items-center gap-2 mt-1 text-[10px] font-bold bg-black/25 rounded-sm px-1 py-0.5"
                                                         title={stats.simulated ? "Simulierte Statistik — echte Werte, sobald der Meta-Token hinterlegt ist" : "Live-Statistik von Meta"}
                                                         data-testid={`social-post-stats-${p.id}`}>
                                                        <span className="inline-flex items-center gap-0.5"><Heart className="w-3 h-3" />{stats.likes}</span>
                                                        <span className="inline-flex items-center gap-0.5"><Eye className="w-3 h-3" />{stats.reach}</span>
                                                        <span className="inline-flex items-center gap-0.5"><MousePointerClick className="w-3 h-3" />{stats.clicks}</span>
                                                        {stats.simulated && <span className="opacity-70">≈</span>}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <p className="text-[11px] text-gray-400 mt-2 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" /> Likes</span>
                    <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> Reichweite</span>
                    <span className="inline-flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> Klicks</span>
                    <span>≈ = simulierte Werte, bis der Meta-Token hinterlegt ist</span>
                </p>
            </div>

            {/* Jul-2026 (User-Wunsch) — Themen-Newsticker aus der Marktbeobachtung */}
            <MarketWatchTicker />
        </div>
    );
}

function SocialSettingsDialog({ open, onClose, settings }) {
    const [token, setToken] = useState("");
    const [fbPage, setFbPage] = useState("");
    const [igUser, setIgUser] = useState("");
    const [wa, setWa] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (open && settings) {
            setFbPage(settings.fb_page_id || "");
            setIgUser(settings.ig_user_id || "");
            setWa(settings.whatsapp_number || "");
            setToken("");
        }
    }, [open, settings]);
    const save = async () => {
        setSaving(true);
        try {
            await api.post("/social/settings", {
                meta_access_token: token || null, fb_page_id: fbPage, ig_user_id: igUser, whatsapp_number: wa,
            });
            toast.success("Einstellungen gespeichert");
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="social-settings-dialog">
                <DialogHeader><DialogTitle className="font-display font-black">Kanäle & Meta-Token</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <p className="text-xs text-gray-600">
                        Ohne Token wird das Posten <b>simuliert</b>. Mit einem Meta Page Access Token
                        (Facebook-Seite / Instagram-Business) postet TwoWheels direkt auf deine Kanäle.
                    </p>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Meta Access Token {settings?.has_meta_token && <span className="text-emerald-600">(hinterlegt)</span>}</label>
                        <Input type="password" value={token} onChange={(e) => setToken(e.target.value)}
                               placeholder={settings?.has_meta_token ? "•••••• (leer lassen = unverändert)" : "EAAB…"} data-testid="social-token-input" />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Facebook Page ID</label>
                        <Input value={fbPage} onChange={(e) => setFbPage(e.target.value)} data-testid="social-fbpage-input" />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Instagram Business Account ID</label>
                        <Input value={igUser} onChange={(e) => setIgUser(e.target.value)} data-testid="social-iguser-input" />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">WhatsApp-Nummer</label>
                        <Input value={wa} onChange={(e) => setWa(e.target.value)} placeholder="+49 …" data-testid="social-wa-input" />
                    </div>
                    <Button onClick={save} disabled={saving} className="bg-[#111827] hover:bg-black text-[#FFCC00] font-bold rounded-sm w-full" data-testid="social-settings-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
